import { Injectable, inject } from '@angular/core';
import { CollectionRequestBuilder, HttpErrorService } from '@web/common';
import {
  TableDataPayload,
  TableLazyLoad,
  ToastService,
  UploadException,
} from '@web/shared/ui';
import { AdminShippingApiService } from '../api/services/admin-shipping-api.service';
import { AdminShippingMethodCollectionFilter } from '../models/admin-shipping-method-collection-filter';
import { AdminShippingMethodCollectionItem } from '../models/admin-shipping-method-collection-item';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  tap,
  throwError,
} from 'rxjs';
import { blank, Guid } from '@web/utils';
import { AdminShippingMethodDetails } from '../models/admin-shipping-method-details';
import { AdminShippingMethodPrice } from '../models/admin-shipping-method-price';
import { dictionaryCountryFromResponse } from '@web/shared/module/dictionary';
import {
  AdminShippingMethodFormData,
  AdminShippingMethodPriceFormData,
} from '../models/admin-shipping-method-form-data';
import { AdminShippingMethodUpdateRequest } from '../api/requests/admin-shipping-method-update.request';
import { AdminShippingMethodPriceRequest } from '../api/requests/admin-shipping-method-price.request';
import { AdminPaymentMethod } from '../models/admin-payment-method';
import { SummernoteImageUpload } from '@web/shared/external';
import { AdminMultimediaDataSourceService } from '@web/shared/module/multimedia';

@Injectable({
  providedIn: 'root',
})
export class AdminShippingMethodDataSourceService {
  private readonly adminShippingApi = inject(AdminShippingApiService);
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly httpError = inject(HttpErrorService);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminShippingMethodCollectionFilter>,
  ): Observable<TableDataPayload<AdminShippingMethodCollectionItem>> {
    /* prettier-ignore */
    return this.adminShippingApi
      .fetchCollection(
        {
          request: this.collectionRequestBuilder
            .page(lazyLoad.page)
            .pageSize(lazyLoad.rows)
            .sorts((builder) =>
              builder.fromTableSort(lazyLoad.sort).buildAsArray(),
            )
            .filters((builder) =>
              builder
                .fromTableFilter(lazyLoad.filter)
                .appendDate((builder) => builder.rangeFor('createdAt').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('name').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('description').build())
                .appendText((builder) => builder.inFor('countries').build())
                .buildAsArray(),
            )
            .build()
        },
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new AdminShippingMethodCollectionItem({
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    id: item.id,
                    name: item.name,
                    code: item.code,
                    description: item.description,
                    countries: item.countries.map((country) => dictionaryCountryFromResponse(country)),
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public uploadEditorImages(
    files: File[],
  ): Observable<SummernoteImageUpload[]> {
    return combineLatest(
      files.map((file) =>
        this.multimediaDataSource
          .upload(file)
          .pipe(
            catchError((response) =>
              throwError(
                () =>
                  new UploadException(
                    this.httpError.extract(response).multiLineMessages(),
                  ),
              ),
            ),
          ),
      ),
    ).pipe(
      map((items) =>
        items.map(
          (item) => new SummernoteImageUpload(item.previewUrl, item.filename),
        ),
      ),
    );
  }

  public fetchDetailsById(
    id: Guid,
    skipByLoading = true,
  ): Observable<AdminShippingMethodDetails> {
    return this.adminShippingApi.fetchDetailsById({ id, skipByLoading }).pipe(
      map(
        (response) =>
          new AdminShippingMethodDetails({
            canBeEdited: response.contextOperations.canBeEdited,
            canBeViewed: response.contextOperations.canBeViewed,
            category: response.category,
            createdAt: response.createdAt,
            code: response.code,
            description: response.description,
            id: response.id,
            name: response.name,
            prices: response.prices.map(
              (price) =>
                new AdminShippingMethodPrice({
                  price: price.price,
                  id: price.id,
                  country: dictionaryCountryFromResponse(price.country),
                  paymentMethods: price.paymentMethods.map(
                    (method) =>
                      new AdminPaymentMethod({
                        id: method.id,
                        name: method.name,
                        code: method.code,
                      }),
                  ),
                }),
            ),
          }),
      ),
    );
  }

  public fetchDetailsFormById(
    id: Guid,
  ): Observable<AdminShippingMethodFormData> {
    return this.adminShippingApi
      .fetchDetailsById({ id, skipByLoading: true })
      .pipe(
        map(
          (response) =>
            new AdminShippingMethodFormData({
              description: response.description,
              name: response.name,
              category: response.category,
              prices: response.prices.map(
                (price) =>
                  new AdminShippingMethodPriceFormData({
                    price: price.price,
                    countryId: price.country.id,
                    paymentMethodIds: price.paymentMethods.map(
                      (method) => method.id,
                    ),
                  }),
              ),
            }),
        ),
      );
  }

  public updateByFormData(
    id: Guid,
    data: AdminShippingMethodFormData,
  ): Observable<void> {
    return this.adminShippingApi
      .update(
        id,
        new AdminShippingMethodUpdateRequest({
          name: data.name,
          category: data.category,
          description: data.description,
          prices: data.prices.map(
            (price) =>
              new AdminShippingMethodPriceRequest({
                countryId: price.countryId,
                paymentMethods: price.paymentMethodIds,
                price: String(price.price),
              }),
          ),
        }),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess('Metoda wysyłki została zaktualizowana.')
              .build(),
          );
        }),
        blank(),
      );
  }
}
